import { type ReactNode, useContext } from 'react';

import dayjs from 'dayjs';

import {
  Button,
  Icons,
  LayoutCard,
  MetadataChipV2,
  Skeleton,
  Typography,
  Stack,
} from '@cast/design-system';
import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  WorkloadDetailsContext,
  configuredByLabels,
  ManifestContent,
  ManifestTabContent,
  useGpuMetricsData,
  useWoopWorkloadNativeVpaSpec,
  useWoopWorkloadRecommendationManifest,
} from '@cast/split-me/features/cluster/workload-optimization';
import { isManagedByAnnotations } from '@cast/split-me/features/workload-optimization';
import { WorkloadManager } from '@cast/types';
import { getTimeAgo } from '@cast/utils';

import { metaTextLinkSx } from './_components/_metaStyles';
import { MetaAnnotations } from './_components/MetaAnnotations';
import { MetaDetailRow, MetaDetailRows } from './_components/MetaDetailRow';
import { MetaLabelsSection } from './_components/MetaLabelsSection';
import { useManifestDrawer } from './_components/useManifestDrawer';
import { usePodListDrawer } from './_components/usePodListDrawer';

const ViewLink = ({
  onClick,
  children,
  testId,
}: {
  onClick: () => void;
  children: ReactNode;
  testId: string;
}) => (
  <Button
    variant="text"
    size="sm"
    onClick={onClick}
    endIcon={<Icons.ArrowRight size={16} weight="regular" />}
    testId={testId}
    sx={metaTextLinkSx}
  >
    {children}
  </Button>
);

const DateWithRelative = ({ value }: { value?: string }) => {
  if (!value) {
    return (
      <Typography variant="P12R" color="textSecondary">
        —
      </Typography>
    );
  }

  const date = dayjs(value);
  if (!date.isValid()) {
    return <Typography variant="P12R">{value}</Typography>;
  }

  const relative = getTimeAgo(value, true);

  return (
    <Stack direction="row" gap={8} alignItems="baseline" flexWrap="wrap">
      <Typography variant="P12R">
        {date.format('MMM D, YYYY, HH:mm:ss')}
      </Typography>
      {relative ? (
        <Typography variant="P10R" color="textSecondary">
          ({relative})
        </Typography>
      ) : null}
    </Stack>
  );
};

const InventoryWorkloadMetaContent = () => {
  const { workload, pods } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { openPodListDrawer } = usePodListDrawer();
  const { openManifestDrawer } = useManifestDrawer();
  const { normalized: gpuNormalized } = useGpuMetricsData();

  const recommendationManifestQuery = useWoopWorkloadRecommendationManifest({
    clusterId: workload?.clusterId ?? '',
    workloadId: workload?.id ?? '',
    enabled: !!workload,
  });

  const vpaSpecQuery = useWoopWorkloadNativeVpaSpec({
    clusterId: workload?.clusterId ?? '',
    workloadId: workload?.id ?? '',
    enabled: !!workload,
  });

  if (!workload) {
    return <Skeleton height={400} width="100%" />;
  }

  const podsLoaded = pods !== undefined;
  const podsCount = podsLoaded ? pods.length : workload.podCount ?? 0;
  const canOpenPods = podsLoaded && pods.length > 0;
  const podsPending = !podsLoaded && (workload.podCount ?? 0) > 0;

  const showRecommendationManifest =
    !!recommendationManifestQuery.error ||
    !!recommendationManifestQuery.data?.object;
  const showVpaManifest = !!vpaSpecQuery.error || !!vpaSpecQuery.data?.object;

  const gpuLabels = gpuNormalized?.gpuDeviceLabels ?? [];
  const configuredBy = isManagedByAnnotations(workload)
    ? configuredByLabels[WorkloadManager.ANNOTATIONS]
    : configuredByLabels[WorkloadManager.API];

  return (
    <LayoutCard testId="inventory-workload-meta">
      <MetaDetailRows>
        <MetaDetailRow label="Cluster" testId="meta-cluster">
          <Stack direction="row" gap={4} alignItems="center">
            <K8sProviderIcon provider={cluster.providerType} size={20} />
            <Typography variant="P12R">{cluster.name}</Typography>
          </Stack>
        </MetaDetailRow>

        <MetaDetailRow label="Pods" testId="meta-pods">
          {canOpenPods ? (
            <ViewLink
              testId="meta-pods-open"
              onClick={() => openPodListDrawer({ pods: pods ?? [], workload })}
            >
              {podsCount}
            </ViewLink>
          ) : podsPending ? (
            <Skeleton width={24} height={16} />
          ) : (
            <Typography variant="P12R" data-testid="meta-pods-count">
              {podsCount}
            </Typography>
          )}
        </MetaDetailRow>

        <MetaDetailRow label="Kind" testId="meta-kind">
          <Typography variant="P12R">
            {workload.isJobLike ? `${workload.kind} (job-like)` : workload.kind}
          </Typography>
        </MetaDetailRow>

        <MetaDetailRow label="Namespace" testId="meta-namespace">
          <Typography variant="P12R">{workload.namespace}</Typography>
        </MetaDetailRow>

        <MetaDetailRow label="Configured by" testId="meta-configured-by">
          <Typography variant="P12R">{configuredBy}</Typography>
        </MetaDetailRow>

        <MetaDetailRow label="Custom" testId="meta-custom">
          <Typography variant="P12R">
            {workload.isCustom ? 'Yes' : 'No'}
          </Typography>
        </MetaDetailRow>

        <MetaDetailRow label="API" testId="meta-api">
          <Typography variant="P12R">
            {`${workload.version}/${workload.group}`}
          </Typography>
        </MetaDetailRow>

        <MetaDetailRow label="Created" testId="meta-created">
          <DateWithRelative value={workload.createdAt} />
        </MetaDetailRow>

        <MetaDetailRow
          label="Workload manifest"
          testId="meta-workload-manifest"
        >
          <ViewLink
            testId="meta-workload-manifest-open"
            onClick={() =>
              openManifestDrawer({
                title: 'Workload manifest',
                children: <ManifestContent workload={workload} />,
              })
            }
          >
            View
          </ViewLink>
        </MetaDetailRow>

        {showRecommendationManifest && (
          <MetaDetailRow
            label="Recommendation manifest"
            testId="meta-recommendation-manifest"
          >
            <ViewLink
              testId="meta-recommendation-manifest-open"
              onClick={() =>
                openManifestDrawer({
                  title: 'Recommendation manifest',
                  children: (
                    <ManifestTabContent
                      data={recommendationManifestQuery.data}
                      error={recommendationManifestQuery.error}
                      refetch={recommendationManifestQuery.refetch}
                      isLoading={recommendationManifestQuery.isLoading}
                      testId="recommendation-manifest-content"
                      loadingTitle="Fetching recommendation manifest"
                    />
                  ),
                })
              }
            >
              View
            </ViewLink>
          </MetaDetailRow>
        )}

        {showVpaManifest && (
          <MetaDetailRow label="VPA manifest" testId="meta-vpa-manifest">
            <ViewLink
              testId="meta-vpa-manifest-open"
              onClick={() =>
                openManifestDrawer({
                  title: 'VPA manifest',
                  children: (
                    <ManifestTabContent
                      data={vpaSpecQuery.data}
                      error={vpaSpecQuery.error}
                      refetch={vpaSpecQuery.refetch}
                      isLoading={vpaSpecQuery.isLoading}
                      testId="vpa-manifest-content"
                      loadingTitle="Fetching VPA manifest"
                    />
                  ),
                })
              }
            >
              View
            </ViewLink>
          </MetaDetailRow>
        )}

        {gpuLabels.length > 0 && (
          <MetaDetailRow label="GPU" testId="meta-gpu">
            <Stack direction="row" gap={4} flexWrap="wrap" alignItems="center">
              {gpuLabels.map(({ name, label }) => (
                <MetadataChipV2
                  key={name || label}
                  label={label}
                  size="l"
                  variant="strong"
                />
              ))}
            </Stack>
          </MetaDetailRow>
        )}

        <MetaLabelsSection labels={workload.labels ?? []} />

        <MetaDetailRow
          label={`Annotations: ${workload.annotations?.length ?? 0}`}
          testId="meta-annotations-row"
        >
          <MetaAnnotations workload={workload} />
        </MetaDetailRow>
      </MetaDetailRows>
    </LayoutCard>
  );
};

export const InventoryWorkloadMeta = () => <InventoryWorkloadMetaContent />;
